import React, { memo, useMemo, useCallback, createContext, useEffect, useContext, useState, useRef } from "react";
import { useExamStorage } from "../hooks/useExamStorage";
import { getExamStorage } from "../utils/examStorageHelper";
import { KEYS } from "../constants/storageKeys";
const ExamContext = createContext();

export const ExamProvider = ({ children }) => {
  const [readingAnswers, setReadingAnswers] = useExamStorage(KEYS.READING_ANSWERS, []);
  const [listeningAnswers, setListeningAnswers] = useExamStorage(KEYS.LISTENING_ANSWERS, []);
  const [writingAnswers, setWritingAnswers] = useExamStorage(KEYS.WRITING_ANSWERS, []);
  const [stage, setStage] = useExamStorage(KEYS.STAGE, 1);
  // const [section, setSection] = useState("reading");
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [activeView, setActiveView] = useState(null);
  const [exam, setExam] = useState(null);
  const [isDemoExam, setIsDemoExam] = useState(false);
  const [isInstantExam, setIsInstantExam] = useState(false);
  const [isManualSubmit, setIsManualSubmit] = useState(false);
  const [productType, setProductType] = useState(false);
  const [productId, setProductId] = useState();
  const [uprId, setUprId] = useState();
  const [examType, setExamType] = useState();
  const answers = useMemo(() => {
    return activeView === "reading" ? readingAnswers : listeningAnswers;
  }, [activeView, readingAnswers, listeningAnswers]);

  const setters = useMemo(() => ({
    reading: setReadingAnswers,
    listening: setListeningAnswers,
    writing: setWritingAnswers,
  }), [setReadingAnswers, setListeningAnswers, setWritingAnswers]);

  const isSectionPractice = ['reading', 'listening', 'writing'].includes(productType);
  const isPractice = examType === "free-practice";
  const isRealExam = ["paid-instant", "paid-scheduled"].includes(examType);

  //STAGES
  const changeStage = (stageNum) => {
    setStage(stageNum);
  };

  const goNextStage = () => {
    setStage((prev) => prev + 1);
  };

  const backPreviousStage = () => {
    setStage((prev) => prev - 1);
  };

  const setDemoExamData = (isDemoExam) => {
    setIsDemoExam(isDemoExam);
  };

  const setInstantExamData = (isInstantExam) => {
    setIsInstantExam(isInstantExam);
  };

  const setProductTypeData = (productType) => {
    setProductType(productType);
  };

  const setProductIdData = (productId) => {
    setProductId(productId);
  };

  const setUprIdData = (uprId) => {
    setUprId(uprId);
  };

  //ANSWERS
  // We use a ref to track activeView 
  const activeViewRef = useRef(activeView);
  activeViewRef.current = activeView;

  const updateAnswer = useCallback((newAnswer) => {
    const currentView = activeViewRef.current;

    if (newAnswer?.answer && typeof newAnswer.answer === "string" && currentView !== "writing") {
      newAnswer = {
        ...newAnswer,
        answer: newAnswer.answer.trim()
      };
    }

    const setAnswers = setters[currentView];

    setAnswers((prevAnswers) => {
      if (currentView !== "writing" && !newAnswer) {
        return prevAnswers.filter((a) => a.questionNumber !== newAnswer?.questionNumber);
      }

      const index = prevAnswers.findIndex(
        (a) => a.questionNumber === newAnswer.questionNumber
      );

      let updatedAnswers;

      if (index !== -1) {
        updatedAnswers = [...prevAnswers];
        updatedAnswers[index] = { ...updatedAnswers[index], ...newAnswer };
      } else {
        updatedAnswers = [...prevAnswers, newAnswer];
      }
      return updatedAnswers.sort((a, b) => a.questionNumber - b.questionNumber);
    });
  }, [setters]);

  const removeAnswer = (questionNumber) => {
    const setAnswers = setters[activeView];
    setAnswers((prevAnswers) => {
      if (!Array.isArray(prevAnswers)) return [];

      const updatedAnswers = prevAnswers.filter((a) => a.questionNumber !== questionNumber);

      return updatedAnswers.length > 0 ? updatedAnswers : [];
    });
  };

  //TIMING
  const updateStageTimeLeft = (time) => {
    setTimeLeft(time);
  };

  //SECTIONS
  const updateActiveView = (stage) => {
    changeStage(1)
    setActiveView(stage);
  };

  //SECTIONS
  const getExam = (exam) => {
    setExam(exam);
  };

  return (
    <ExamContext.Provider
      value={{
        answers,
        writingAnswers,
        timeLeft,
        exam,
        isRealExam,
        isPractice,
        isSectionPractice,
        uprId,
        productId,
        productType,
        setUprIdData,
        setProductIdData,
        setProductTypeData,
        getExam,
        examType,
        setExamType,
        isManualSubmit,
        setIsManualSubmit,
        updateStageTimeLeft,
        isDemoExam,
        isInstantExam,
        setReadingAnswers,
        setListeningAnswers,
        setWritingAnswers,
        setIsDemoExam,
        setDemoExamData,
        setIsInstantExam,
        setInstantExamData,
        activeView,
        updateActiveView,
        removeAnswer,
        updateAnswer,
        writingAnswers,
        listeningAnswers,
        readingAnswers,
        // updateWritingAnswer,
        setStage,
        stage,
        changeStage,
        goNextStage,
        backPreviousStage,
        setActiveQuestion,
        activeQuestion,
      }}
    >
      {children}
    </ExamContext.Provider>
  );
};

export const useExam = () => useContext(ExamContext);
